import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'

/** Teacher dashboard data. Every endpoint is role-gated server-side. */

export interface AdminStudent {
  id: string
  displayName: string
  email: string
  level: string | null
  travelDate: string | null
  totalXp: number
  currentStreak: number
  longestStreak: number
  lessonsCompleted: number
  lastActiveDate: string | null
}

export interface AdminUnitProgress {
  id: string
  title: string
  position: number
  total: number
  done: number
  avgScore: number | null
}

export interface AdminSkill {
  tag: string
  label: string
  attempts: number
  accuracyPct: number
}

export interface AdminActivityDay {
  local_date: string
  xp: number
  minutes: number
  lessons_completed: number
  reviews_done: number
}

export interface AdminStudentDetail {
  student: AdminStudent
  units: AdminUnitProgress[]
  skills: AdminSkill[]
  activity: AdminActivityDay[]
  mastery: Record<string, number>
  words: AdminWordTotals
  /** Devices subscribed to notifications. Zero means nothing can be sent. */
  pushDevices: number
}

/** Whether review is keeping pace with what lessons have taught. */
export interface AdminWordTotals {
  /** Scheduled reviews that have come due. */
  due: number
  /** Taught by a finished lesson, never once recalled. */
  newAvailable: number
  introducedToday: number
}

export interface AdminMistake {
  exerciseId: string
  prompt: string
  type: string
  skillTag: string | null
  skillLabel: string | null
  lessonTitle: string
  unitTitle: string
  wrong: number
  attempts: number
  accuracyPct: number
}

export interface AdminVocabRow {
  id: string
  pt: string
  en: string
  category: string
  part_of_speech: string | null
  gender: string | null
  example_pt: string | null
  example_en: string | null
  region_note: string | null
  trip_critical: number
  difficulty: number
  edited_at: number | null
}

export interface AdminExercise {
  id: string
  prompt: string
  type: string
  difficulty: number
  skillTag: string | null
  lessonTitle: string
  editedAt: number | null
  payload: unknown
}

export function useStudents() {
  return useQuery({
    queryKey: ['admin', 'students'],
    queryFn: () => api<{ students: AdminStudent[] }>('/admin/students'),
    staleTime: 60_000,
  })
}

export function useStudentDetail(userId: string | null) {
  return useQuery({
    queryKey: ['admin', 'student', userId],
    queryFn: () => api<AdminStudentDetail>(`/admin/students/${userId ?? ''}`),
    enabled: Boolean(userId),
    staleTime: 30_000,
  })
}

export interface PushSendReport {
  sent: number
  gone: number
  failed: number
}

/**
 * Sends a one-off notification to a student's devices.
 *
 * No optimistic update and no retry: a push is not idempotent, and a silent
 * retry on a flaky connection is how one message arrives twice.
 */
export function useSendPush(userId: string | null) {
  return useMutation({
    retry: false,
    mutationFn: (input: { title: string; body: string; url?: string }) =>
      api<PushSendReport>(`/admin/students/${userId ?? ''}/push`, {
        method: 'POST',
        body: input,
      }),
  })
}

export function useMistakes(userId: string | null) {
  return useQuery({
    queryKey: ['admin', 'mistakes', userId],
    queryFn: () => api<{ mistakes: AdminMistake[] }>(`/admin/students/${userId ?? ''}/mistakes`),
    enabled: Boolean(userId),
    staleTime: 30_000,
  })
}

export function useWrongAnswers(userId: string | null, exerciseId: string | null) {
  return useQuery({
    queryKey: ['admin', 'wrong', userId, exerciseId],
    queryFn: () =>
      api<{ answers: Array<{ given: unknown; createdAt: number }> }>(
        `/admin/students/${userId ?? ''}/mistakes/${exerciseId ?? ''}`,
      ),
    enabled: Boolean(userId && exerciseId),
  })
}

export function useVocabularySearch(query: string) {
  return useQuery({
    queryKey: ['admin', 'vocabulary', query],
    queryFn: () =>
      api<{ vocabulary: AdminVocabRow[] }>(
        `/admin/vocabulary?q=${encodeURIComponent(query)}`,
      ),
    staleTime: 15_000,
  })
}

export function useUpdateVocabulary() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api<{ ok: true }>(`/admin/vocabulary/${id}`, { method: 'PATCH', body }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'vocabulary'] })
      // The phrasebook reads the same rows, so its cache is now stale.
      await queryClient.invalidateQueries({ queryKey: ['phrasebook'] })
    },
  })
}

export function useExercise(id: string | null) {
  return useQuery({
    queryKey: ['admin', 'exercise', id],
    queryFn: () => api<{ exercise: AdminExercise }>(`/admin/exercises/${id ?? ''}`),
    enabled: Boolean(id),
  })
}

export function useUpdateExercise() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api<{ ok: true }>(`/admin/exercises/${id}`, { method: 'PATCH', body }),
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'exercise', vars.id] })
    },
  })
}
